import { forwardRef } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { formatNumber } from '../../../utils/formatters';

// Risk level colors
const RISK_COLORS = {
  CRITICAL: '#e74c3c',    // Red - for imminent, suicide, safety
  HIGH: '#f39c12',        // Orange - for risk, depression, anxiety
  MONITOR: '#3498db',     // Blue - default/monitor
  HELP_REQUESTED: '#27ae60', // Green - for help, requested, seeking
};

const useStyles = makeStyles((theme) => ({
  exportWrapper: {
    position: 'absolute',
    top: -9999,
    left: -9999,
    width: 1250,
    minHeight: 950,
    backgroundColor: '#ffffff',
    padding: 35,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  clinicalHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 30,
    marginBottom: 30,
    alignItems: 'flex-start',
  },
  titleSection: {
    background: 'white',
    padding: 25,
    borderRadius: 12,
    borderLeft: '4px solid #5b65dc',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  mainTitle: {
    fontSize: 24,
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: 8,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 15,
    lineHeight: 1.4,
  },
  paramsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 15,
  },
  paramBox: {
    background: '#f1f5f9',
    padding: 12,
    borderRadius: 6,
  },
  paramLabel: {
    fontSize: 10,
    color: '#64748b',
    textTransform: 'uppercase',
    fontWeight: 600,
    marginBottom: 4,
    letterSpacing: '0.5px',
  },
  paramValue: {
    fontSize: 13,
    color: '#0f172a',
    fontWeight: 600,
  },
  brandingSection: {
    background: 'white',
    padding: 20,
    borderRadius: 12,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    minWidth: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logoContainer: {
    width: 120,
    height: 'auto',
    marginBottom: 12,
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  website: {
    fontSize: 12,
    color: '#5b65dc',
    fontWeight: 500,
    textDecoration: 'none',
    marginBottom: 8,
    display: 'block',
  },
  badge: {
    background: '#dbeafe',
    color: '#1d4ed8',
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  chartContainer: {
    flex: 1,
    background: 'white',
    borderRadius: '12px 12px 0 0',
    padding: '20px 20px 20px 20px', // Increased bottom padding for X-axis labels
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    maxHeight: 480, // Increased height for chart area to accommodate X-axis
    overflow: 'visible', // Ensure X-axis labels are not clipped
  },
  legend: {
    background: 'white',
    borderRadius: '0 0 12px 12px',
    borderTop: '1px solid #E2E8F0',
    padding: '15px 20px',
    display: 'flex',
    justifyContent: 'center',
    gap: 24,
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    borderRadius: 16,
    background: 'rgba(255, 255, 255, 0.8)',
    border: '1px solid rgba(226, 232, 240, 0.6)',
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    border: '2px solid rgba(255, 255, 255, 0.8)',
  },
  legendLabel: {
    fontSize: 11,
    color: '#374151',
    fontWeight: 600,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
  },
  footnote: {
    marginTop: 15,
    fontSize: 9,
    color: '#94a3b8',
    lineHeight: 1.4,
    textAlign: 'center',
  },
  visible: {
    position: 'static',
    top: 'auto',
    left: 'auto',
  },
}));

const QHSLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1360 460">
    <title>Verdin Labs Logo</title>
    <g transform="translate(57.68,-170.4) scale(0.749)" fill="#122056">
      <path d="M263.286,773.994c-8.678,0.66 -22.53,1.108 -39.136,-2.128c-34.642,-6.75 -46.976,-14.491 -62.397,-22.839c-4.333,-2.346 -10.046,-6.81 -12.365,-8.162c-9.794,-5.71 -34.728,-30.733 -39.731,-37.527c-3.993,-5.421 -8.584,-10.451 -9.42,-11.647c-5.747,-8.224 -6.516,-7.554 -9.477,-11.611c-8.15,-11.166 -8.527,-10.827 -9.199,-11.819c-4.987,-7.364 -24.607,-30.212 -33.496,-36.357c-22.05,-15.245 -44.963,-8.245 -44.963,-8.245c0,0 0.408,-5.482 0.655,-7.048c2.954,-18.773 16.503,-33.938 21.64,-37.427c4.463,-3.031 8.41,-7.449 18.838,-11.505c12.784,-4.972 19.087,-4.574 28.001,-4.116c18.992,0.976 31.63,11.472 31.63,11.472c0,0 -3.661,8.616 -4.938,13.85c-5.323,21.825 6.916,53.242 6.916,53.242c0,0 -0.031,-7.321 -0.749,-25.387c-1.151,-28.992 9.077,-40.701 9.795,-41.788c8.733,-13.224 13.222,-16.835 30.111,-28.086c6.182,-4.118 23.762,-12.117 25.262,-12.72c8.278,-3.328 27.55,-9.955 38.085,-14.785c11.397,-5.224 41.623,-16.228 46.146,-17.748c37.166,-12.486 68.136,-50.474 68.136,-50.474c0,0 2.609,32.112 1.147,43.841c-1.946,15.614 -2.443,17.525 -2.83,19.013c-4.288,16.497 -6.068,23.569 -9.763,30.413c-6.374,11.809 -10.707,18.915 -18.197,26.367c-13.206,13.139 -26.662,20.249 -29.243,22.03c-12.489,8.613 -21.899,11.237 -36.346,24.73c-14.539,13.58 -16.438,25.721 -18.171,31.677c-10.664,36.641 5.618,74.526 8.71,79.443c0.661,1.051 4.449,8.728 12.21,18.141c7.067,8.571 21.802,22.289 33.136,27.2Z"/>
      <path d="M127.073,521.7c-14.223,15.339 -21.227,28.995 -21.227,28.995c0,0 8.125,-39.008 17.407,-53.546c5.479,-8.58 14.308,-28.785 48.892,-56.845c1.22,-0.989 12.433,-10.087 15.423,-12.145c6.426,-4.42 6.142,-4.742 12.721,-8.838c5.53,-3.444 5.044,-4.07 10.657,-7.449c3.134,-1.886 42.984,-27.916 48.28,-31.661c20.67,-14.616 24.64,-18.254 26.605,-20.055c1.18,-1.082 1.495,-0.683 15.185,-13.033c11.812,-10.656 21.532,-23.431 23.207,-25.632c14.219,-18.687 17.26,-27.206 17.26,-27.206c0,0 1.073,25.895 -7.707,53.272c-2.229,6.951 -10.121,24.298 -14.17,30.16c-4.345,6.289 -4.138,7.681 -11.387,16.524c-4.21,5.135 -3.937,5.311 -8.525,10.1c-2.16,2.254 -2.692,3.431 -10.317,10.752c-20.218,19.411 -35.246,29.986 -90.282,59.914c-8.873,4.825 -29.85,15.001 -55.987,32.769c-1.029,0.7 -12.258,9.852 -16.034,13.925Z"/>
    </g>
    <text x="368" y="298" fontFamily="'Segoe UI', 'Helvetica Neue', Arial, sans-serif" fontSize="188" letterSpacing="-3" fill="#122056"><tspan fontWeight="700">Verdin</tspan><tspan fontWeight="400" fill="#5B65DC"> Labs</tspan></text>
  </svg>
);

const ExportableChartWrapper = forwardRef(({
  children,
  title = "Population Health Assessment Report",
  subtitle = "Comprehensive patient outcome analysis and clinical metrics overview",
  dateRange,
  selectedInstrument,
  isComparisonMode,
  totalPatients,
  visible = false
}, ref) => {
  const classes = useStyles();

  const formatDateRange = () => {
    if (!dateRange?.startDate || !dateRange?.endDate) return 'All time';
    const start = new Date(dateRange.startDate).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
    const end = new Date(dateRange.endDate).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
    return `${start} - ${end}`;
  };

  const getInstrumentName = () => {
    const instrumentMap = {
      'phq9': 'PHQ-9 (Depression)',
      'gad7': 'GAD-7 (Anxiety)',
      'pcptsd': 'PC-PTSD-5',
      'audit': 'AUDIT (Alcohol)',
      'dast': 'DAST-10 (Substance)',
    };
    return instrumentMap[selectedInstrument] || selectedInstrument || 'All Instruments';
  };

  return (
    <div ref={ref} className={`${classes.exportWrapper} ${visible ? classes.visible : ''}`}>
      {/* Header */}
      <div className={classes.clinicalHeader}>
        <div className={classes.titleSection}>
          <Typography className={classes.mainTitle}>
            {title}
          </Typography>
          <Typography className={classes.subtitle}>
            {subtitle}
          </Typography>

          <div className={classes.paramsGrid}>
            <div className={classes.paramBox}>
              <div className={classes.paramLabel}>Reporting Period</div>
              <div className={classes.paramValue}>{formatDateRange()}</div>
            </div>
            <div className={classes.paramBox}>
              <div className={classes.paramLabel}>Assessment Tool</div>
              <div className={classes.paramValue}>{getInstrumentName()}</div>
            </div>
            <div className={classes.paramBox}>
              <div className={classes.paramLabel}>Patient Cohort</div>
              <div className={classes.paramValue}>{formatNumber(totalPatients) || 'N/A'} Active Patients</div>
            </div>
            <div className={classes.paramBox}>
              <div className={classes.paramLabel}>Analysis Type</div>
              <div className={classes.paramValue}>
                {isComparisonMode ? 'Comparative Analysis' : 'Current Period'}
              </div>
            </div>
            <div className={classes.paramBox}>
              <div className={classes.paramLabel}>Data Status</div>
              <div className={classes.paramValue}>De-identified</div>
            </div>
            <div className={classes.paramBox}>
              <div className={classes.paramLabel}>Generated</div>
              <div className={classes.paramValue}>
                {new Date().toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>

        <div className={classes.brandingSection}>
          <div className={classes.logoContainer}>
            <QHSLogo />
          </div>
          <Typography className={classes.website}>
            www.verdinlabs.com
          </Typography>
          <div className={classes.badge}>
            Clinical Grade
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className={classes.chartContainer}>
        {children}
      </div>

      {/* Legend */}
      <div className={classes.legend}>
        {isComparisonMode ? (
          <>
            <div className={classes.legendItem}>
              <div
                className={classes.legendColor}
                style={{ backgroundColor: '#3498db', opacity: 0.6 }}
              />
              <span className={classes.legendLabel}>Baseline (Light)</span>
            </div>
            <div className={classes.legendItem}>
              <div
                className={classes.legendColor}
                style={{ backgroundColor: '#3498db' }}
              />
              <span className={classes.legendLabel}>Follow-up (Full Color)</span>
            </div>
          </>
        ) : (
          <>
            <div className={classes.legendItem}>
              <div
                className={classes.legendColor}
                style={{ backgroundColor: RISK_COLORS.CRITICAL }}
              />
              <span className={classes.legendLabel}>Critical</span>
            </div>
            <div className={classes.legendItem}>
              <div
                className={classes.legendColor}
                style={{ backgroundColor: RISK_COLORS.HIGH }}
              />
              <span className={classes.legendLabel}>High</span>
            </div>
            <div className={classes.legendItem}>
              <div
                className={classes.legendColor}
                style={{ backgroundColor: RISK_COLORS.MONITOR }}
              />
              <span className={classes.legendLabel}>Monitor</span>
            </div>
            <div className={classes.legendItem}>
              <div
                className={classes.legendColor}
                style={{ backgroundColor: RISK_COLORS.HELP_REQUESTED }}
              />
              <span className={classes.legendLabel}>Help Requested</span>
            </div>
          </>
        )}
      </div>

      <Typography className={classes.footnote}>
        Clinical Data Disclaimer: This report contains aggregated, de-identified patient health information processed in compliance with HIPAA Privacy Rule.
        Data is intended for healthcare quality improvement and clinical decision support. Individual patient identifiers have been removed to protect privacy.
      </Typography>
    </div>
  );
});

ExportableChartWrapper.displayName = 'ExportableChartWrapper';

export default ExportableChartWrapper;
